import { type IWebMessage, PROTOCOL_VERSION } from './protocol';

/** Максимальный размер входящего сообщения (защита от раздувания). */
export const MAX_MESSAGE_BYTES = 256 * 1024;

export interface IParseOk {
  ok: true;
  message: IWebMessage;
}

export interface IParseErr {
  ok: false;
  code: 'INVALID_MESSAGE' | 'UNSUPPORTED_VERSION';
  reason: string;
}

export type TParseResult = IParseOk | IParseErr;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Разбирает и валидирует сырое сообщение из `message`-события iframe.
 * В отличие от нативки (там приходит строка), postMessage может доставить как строку
 * (сервис шлёт JSON.stringify через __nativeBridge.send), так и уже разобранный объект —
 * поддерживаем оба варианта. Чистая функция, строгая проверка конверта.
 */
export function parseWebMessage(raw: unknown): TParseResult {
  let data: unknown = raw;

  if (typeof raw === 'string') {
    if (raw.length > MAX_MESSAGE_BYTES) {
      return {
        ok: false,
        code: 'INVALID_MESSAGE',
        reason: 'message too large'
      };
    }
    try {
      data = JSON.parse(raw);
    } catch {
      return { ok: false, code: 'INVALID_MESSAGE', reason: 'invalid JSON' };
    }
  }

  if (!isPlainObject(data)) {
    return {
      ok: false,
      code: 'INVALID_MESSAGE',
      reason: 'message is not an object'
    };
  }

  const { v, action, payload, id } = data;

  if (typeof action !== 'string' || action.length === 0) {
    return {
      ok: false,
      code: 'INVALID_MESSAGE',
      reason: 'missing or invalid action'
    };
  }
  if (id !== undefined && typeof id !== 'string') {
    return {
      ok: false,
      code: 'INVALID_MESSAGE',
      reason: 'id must be a string'
    };
  }
  if (v !== PROTOCOL_VERSION) {
    return {
      ok: false,
      code: 'UNSUPPORTED_VERSION',
      reason: `expected protocol v${PROTOCOL_VERSION}`
    };
  }

  const message: IWebMessage = { v: PROTOCOL_VERSION, action, payload };
  if (id !== undefined) message.id = id as string;
  return { ok: true, message };
}
